/**
 * Compact visual theme picker for onboarding.
 */
import * as React from 'react';
import { BOOKING_THEMES, FLOW_DEFAULTS, type BookingTheme } from '@/components/booking-flow/themes';
import { V2C, fontBody } from './tokens';
import { V2Label } from './primitives';

const visuallyHidden: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

function swatchColors(theme: BookingTheme) {
  return {
    page: theme.flowBg || FLOW_DEFAULTS.bg,
    mark: theme.libCta || theme.bg,
    avatar: theme.libAvatar || theme.bg,
    hairline: theme.flowHairline || FLOW_DEFAULTS.hairline,
  };
}

function isVeryLight(color: string): boolean {
  const hex = color.replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return false;
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return luminance > 0.9;
}

interface V2ThemePickerProps {
  value: string;
  onChange: (themeKey: string) => void;
  label?: string;
  onboardingField?: string;
}

export default function V2ThemePicker({
  value,
  onChange,
  label = 'Theme',
  onboardingField,
}: V2ThemePickerProps) {
  const groupName = React.useId();
  const [focusedKey, setFocusedKey] = React.useState<string | null>(null);
  const themes = React.useMemo(() => Object.values(BOOKING_THEMES), []);

  return (
    <div style={{ marginBottom: 8 }}>
      {label && <V2Label>{label}</V2Label>}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          gap: '8px 8px',
        }}
      >
        {themes.map((theme) => {
          const selected = theme.key === value;
          const focused = theme.key === focusedKey;
          const c = swatchColors(theme);
          const lightPage = isVeryLight(c.page);
          const lightAvatar = isVeryLight(c.avatar);

          return (
            <label
              key={theme.key}
              title={theme.name}
              style={{ cursor: 'pointer', display: 'block', minWidth: 0 }}
            >
              <input
                type="radio"
                name={groupName}
                value={theme.key}
                checked={selected}
                onChange={() => onChange(theme.key)}
                onFocus={() => setFocusedKey(theme.key)}
                onBlur={() => setFocusedKey(null)}
                data-onboarding-field={onboardingField}
                style={visuallyHidden}
              />
              <div
                aria-hidden="true"
                style={{
                  height: 48,
                  borderRadius: 12,
                  background: c.page,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: selected
                    ? `2px solid ${V2C.brand}`
                    : `1px solid ${lightPage ? 'rgba(67,63,120,.18)' : 'rgba(255,255,255,.16)'}`,
                  boxShadow: selected
                    ? '0 5px 14px rgba(89,86,246,.20)'
                    : focused
                      ? `0 0 0 2px ${V2C.brandSoft}, 0 3px 10px rgba(20,18,60,.10)`
                      : '0 3px 10px rgba(20,18,60,.10)',
                  transition: 'box-shadow 0.16s ease, transform 0.16s ease, border-color 0.16s ease',
                  transform: selected ? 'translateY(-1px)' : 'none',
                }}
              >
                <div
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    background: c.avatar,
                    border: `1px solid ${lightAvatar ? 'rgba(67,63,120,.18)' : c.hairline}`,
                    boxShadow: `0 0 0 6px ${lightAvatar ? 'rgba(67,63,120,.08)' : `${c.mark}24`}`,
                  }}
                />
              </div>
              <div
                style={{
                  marginTop: 4,
                  fontFamily: fontBody,
                  fontSize: 10.5,
                  lineHeight: 1.15,
                  fontWeight: selected ? 800 : 600,
                  color: selected ? V2C.ink : V2C.mute,
                  textAlign: 'center',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {theme.name}
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
}
